import { z } from 'zod';
import { addDaysIso, minutesOfDay, zonedParts, zonedToUtc } from './time.js';
/**
 * What people choose about notifications (docs/adr/0048-notification-preferences-and-do-not-disturb.md).
 * Types group the stored kinds; each type has three channels. The matrix is a setting (docs/adr/0007)
 * merged cell by cell: default ← workspace ← person.
 */
export const NotificationType = z.enum([
    'mentions',
    'replies',
    'assigned',
    'task_comments',
    'task_due',
    'reminders',
    'support',
    'status_updates',
    'daily_summary',
    'catch_ups',
    'commit_nudges',
    'design_feedback',
    'design_updates',
    'my_day_reminders',
    'rules',
]);
/**
 * In app, push (phone, desktop, browser), email at once, and the daily digest: the type's unread count in the person's
 * start-of-day summary (SC-20). The digest only reaches the team (guests get no summary).
 */
export const NotificationChannel = z.enum(['inApp', 'push', 'email', 'digest']);
export const ChannelPrefs = z.object({ inApp: z.boolean(), push: z.boolean(), email: z.boolean(), digest: z.boolean() });
/** Stored at workspace or person level: only the cells someone chose. */
export const NotificationMatrix = z.partialRecord(NotificationType, ChannelPrefs.partial());
export const NOTIFICATION_TYPES = {
    mentions: {
        label: 'Mentions & direct messages',
        description: 'Someone @mentions you or sends you a direct message.',
        kinds: ['mention'],
        team: false,
        // Unread mentions were always in the start-of-day summary; the digest cell now lets people turn that off.
        defaults: { inApp: true, push: true, email: true, digest: true },
    },
    replies: {
        label: 'Replies',
        description: 'Someone replies to your message.',
        kinds: ['reply'],
        team: false,
        // Unread replies were counted with mentions in the start-of-day summary; kept on by default.
        defaults: { inApp: true, push: true, email: false, digest: true },
    },
    assigned: {
        label: 'Assigned to me',
        description: 'A task or ticket is assigned to you.',
        kinds: ['assigned'],
        team: true,
        feature: 'tasks',
        defaults: { inApp: true, push: true, email: true, digest: false },
    },
    task_comments: {
        label: 'Comments on my tasks',
        description: 'Someone comments on a task you are assigned, created or commented on.',
        kinds: ['task_comment'],
        team: false,
        feature: 'tasks',
        defaults: { inApp: true, push: false, email: true, digest: false },
    },
    task_due: {
        label: 'Task overdue / due soon',
        description: 'A task assigned to you is due tomorrow, or has become overdue.',
        kinds: ['task_overdue', 'task_due_soon'],
        team: true,
        feature: 'tasks',
        defaults: { inApp: true, push: false, email: true, digest: false },
    },
    reminders: {
        label: 'Reminders',
        description: 'Reminders you or a teammate set, and when someone you asked about is free.',
        kinds: ['reminder', 'status_free'],
        team: false,
        defaults: { inApp: true, push: true, email: false, digest: false },
    },
    support: {
        label: 'Support & SLA',
        description: 'A ticket is about to breach its SLA, a customer replies by email, or a support plan runs low.',
        kinds: ['sla_warning', 'ticket_email', 'support_hours'],
        team: true,
        feature: 'support',
        defaults: { inApp: true, push: true, email: true, digest: false },
    },
    status_updates: {
        label: 'Status updates',
        description: 'A task you are assigned or reported moves to another status.',
        kinds: ['status_changed'],
        team: true,
        feature: 'tasks',
        defaults: { inApp: true, push: false, email: false, digest: false },
    },
    daily_summary: {
        label: 'Daily summary',
        description: 'One note each working morning: overdue, due today, gone quiet and unread mentions.',
        kinds: ['daily_summary'],
        team: true,
        defaults: { inApp: true, push: false, email: true, digest: false },
        noDigest: true,
    },
    catch_ups: {
        label: 'Catch-ups',
        description: 'Someone starts a catch-up in a channel you are in.',
        kinds: ['catch_up_started'],
        team: false,
        feature: 'catchups',
        defaults: { inApp: true, push: true, email: false, digest: false },
    },
    commit_nudges: {
        label: 'Commit nudges',
        description: 'A task you are working on has nothing pushed to it for a while, or your day ends with nothing pushed.',
        kinds: ['commit_nudge', 'commit_nudge_lead', 'unpushed_work'],
        team: true,
        feature: 'repos',
        defaults: { inApp: true, push: true, email: false, digest: false },
    },
    // Showcase (docs/adr/0068). Feedback is emailed as a digest every 30 minutes while comments come in.
    design_feedback: {
        label: 'Design feedback',
        description: 'Someone comments on designs for a project you are on, mentions you in a comment, or approves designs.',
        kinds: ['design_comment', 'design_mention', 'design_approved'],
        team: true,
        feature: 'showcase',
        defaults: { inApp: true, push: true, email: true, digest: false },
    },
    design_updates: {
        label: 'Design replies and reviews',
        description: 'Someone replies to your design comment or marks it done, designs or a new version are shared with you, or your feedback is due.',
        kinds: ['design_reply', 'design_shared', 'design_review_due', 'design_version'],
        team: false,
        feature: 'showcase',
        defaults: { inApp: true, push: true, email: true, digest: false },
    },
    // My day (docs/adr/0107): a nudge at the time on your own to-dos. Push reaches the phone, the browser and the desktop app.
    my_day_reminders: {
        label: 'My day reminders',
        description: 'A nudge at the time you gave a to-do on My day, or the minutes before it you chose.',
        kinds: ['todo_nudge'],
        team: true,
        feature: 'todo',
        defaults: { inApp: true, push: true, email: false, digest: false },
    },
    // Project rules (docs/adr/0109): a rule's notify step.
    rules: {
        label: 'Rules',
        description: 'A project rule tells you about a task: it is ready for you, overdue, or needs you next.',
        kinds: ['rule'],
        team: true,
        feature: 'rules',
        defaults: { inApp: true, push: true, email: false, digest: false },
    },
};
/** The type a stored kind belongs to; null for safety notices (always in app and pushed, never emailed). */
export function typeOfKind(kind) {
    if (kind === 'thread_reply')
        return 'replies';
    for (const t of NotificationType.options)
        if (NOTIFICATION_TYPES[t].kinds.includes(kind))
            return t;
    return null;
}
/**
 * Every type's three channels for one person: defaults, then the workspace's cells, then the person's.
 * A type the workspace switched off is off on every channel. Guests get team types switched off.
 */
export function resolveNotificationPrefs(input) {
    const out = {};
    for (const t of NotificationType.options) {
        const def = NOTIFICATION_TYPES[t];
        const cell = { ...def.defaults, ...clean(input.workspace?.[t]) };
        if (t === 'daily_summary' && input.legacySummaryEmail !== undefined && input.person?.[t]?.email === undefined)
            cell.email = input.legacySummaryEmail;
        Object.assign(cell, clean(input.person?.[t]));
        const disabled = !!input.disabled?.includes(t) || (!!input.guest && def.team);
        // Guests get no start-of-day summary, so no digest; the summary type has no digest of its own.
        if (input.guest || def.noDigest)
            cell.digest = false;
        out[t] = disabled ? { inApp: false, push: false, email: false, digest: false, disabled: true } : { ...cell, disabled: false };
    }
    return out;
}
function clean(cell) {
    if (!cell)
        return {};
    const out = {};
    for (const c of NotificationChannel.options)
        if (typeof cell[c] === 'boolean')
            out[c] = cell[c];
    return out;
}
/** Merges two stored matrices cell by cell (the settings resolver uses this for `notifications.matrix`). */
export function mergeMatrix(base, over) {
    const out = { ...base };
    for (const t of NotificationType.options) {
        if (over[t])
            out[t] = { ...(base[t] ?? {}), ...clean(over[t]) };
    }
    return out;
}
// --- Quiet hours ------------------------------------------------------------------------------------
const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const QuietHours = z.object({
    enabled: z.boolean(),
    /** Start and end on the person's clock; an end before the start runs past midnight (18:00 to 08:00). */
    from: HHMM,
    to: HHMM,
    /** All of Saturday and Sunday too. */
    weekends: z.boolean(),
});
export const DEFAULT_QUIET_HOURS = { enabled: false, from: '18:00', to: '08:00', weekends: true };
/** Whether `now` falls in the person's quiet hours, read in their time zone. */
export function inQuietHours(q, now, timeZone) {
    if (!q?.enabled)
        return false;
    const p = zonedParts(now, timeZone);
    if (q.weekends && (p.weekday === 0 || p.weekday === 6))
        return true;
    const m = p.hour * 60 + p.minute;
    const from = minutesOfDay(q.from);
    const to = minutesOfDay(q.to);
    if (from === to)
        return false;
    return from < to ? m >= from && m < to : m >= from || m < to;
}
// --- Pause and presence -----------------------------------------------------------------------------
/** What a person chose besides automatic online and offline. Offline means "appear offline". */
export const PresenceMode = z.enum(['auto', 'busy', 'away', 'offline']);
export const PRESENCE_MODES = {
    auto: { label: 'Automatic', hint: 'Online while Chapsi is open' },
    busy: { label: 'Busy', hint: 'A red dot; people can still message you' },
    away: { label: 'Away', hint: 'Shown as away even while Chapsi is open' },
    offline: { label: 'Appear offline', hint: 'Nobody sees you online' },
};
/** Your own availability (only you see the details). */
export const MyAvailability = z.object({
    mode: PresenceMode,
    paused: z.boolean(),
    /** When the pause ends; null while paused means until you turn it back on (or your status clears). */
    pausedUntil: z.string().nullable(),
    pauseSource: z.enum(['manual', 'status', 'presence']).nullable(),
    /** In quiet hours right now (from your settings). */
    quiet: z.boolean(),
});
/** What others see: busy or away (never "appear offline"), and a moon while notifications are paused. */
export const PersonAvailability = z.object({
    userId: z.uuid(),
    mode: z.enum(['busy', 'away']).nullable(),
    paused: z.boolean(),
});
export const PauseNotificationsInput = z.object({
    /** When to resume; null pauses until you resume by hand. */
    until: z.iso.datetime({ offset: true }).nullable(),
});
export const SetPresenceInput = z.object({
    mode: PresenceMode,
    /** With busy: also pause notifications until you change it back. */
    pauseNotifications: z.boolean().optional(),
});
export const PAUSE_PRESETS = [
    { key: '30m', label: '30 minutes' },
    { key: '1h', label: '1 hour' },
    { key: '2h', label: '2 hours' },
    { key: 'tomorrow', label: 'Until tomorrow morning' },
];
/** "Tomorrow morning" is 8:00 on the person's clock. */
export const MORNING_HOUR = 8;
export function pauseUntilFor(preset, timeZone, now = new Date()) {
    if (preset === '30m')
        return new Date(now.getTime() + 30 * 60_000);
    if (preset === '1h')
        return new Date(now.getTime() + 60 * 60_000);
    if (preset === '2h')
        return new Date(now.getTime() + 120 * 60_000);
    const p = zonedParts(now, timeZone);
    const today = `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
    const [y, m, d] = addDaysIso(today, 1).split('-').map(Number);
    return zonedToUtc(y, m, d, MORNING_HOUR, 0, timeZone);
}
/** "While you were away: 3 mentions and 1 direct message". */
export function awayDigestTitle(held) {
    const parts = [];
    const n = held.mentions ?? 0;
    const d = held.dms ?? 0;
    if (n)
        parts.push(`${n} ${n === 1 ? 'mention' : 'mentions'}`);
    if (d)
        parts.push(`${d} direct ${d === 1 ? 'message' : 'messages'}`);
    return parts.length ? `While you were away: ${parts.join(' and ')}` : null;
}
// --- Web Push ---------------------------------------------------------------------------------------
/** A browser's push subscription (PushSubscription.toJSON()). The endpoint is the push service's URL. */
export const WebPushSubscriptionInput = z.object({
    endpoint: z.url().max(1000).refine((u) => u.startsWith('https://'), 'Must be https'),
    keys: z.object({ p256dh: z.string().min(20).max(200), auth: z.string().min(8).max(100) }),
});
export const RemoveWebPushInput = z.object({ endpoint: z.url().max(1000) });
export const WebPushConfig = z.object({
    /** VAPID public key (base64url), or null when this server has no Web Push. */
    publicKey: z.string().nullable(),
});
// --- Unsubscribe ------------------------------------------------------------------------------------
export const UnsubscribeQuery = z.object({ token: z.string().min(20).max(300) });
export const UnsubscribeInfo = z.object({ type: NotificationType, label: z.string(), workspace: z.string() });
export const UnsubscribeResult = z.object({ ok: z.literal(true), type: NotificationType, label: z.string() });
// --- Shared by the web and phone settings screens ---------------------------------------------------
/** Rows a person sees: no team types for guests, nothing for a feature that is off. */
export function visibleNotificationTypes(opts) {
    return NotificationType.options.filter((t) => {
        const def = NOTIFICATION_TYPES[t];
        return !(opts.guest && def.team) && !(def.feature && !opts.feature(def.feature));
    });
}
/** A stored matrix with one cell changed. Unknown or broken stored values start again from empty. */
export function setMatrixCell(stored, type, channel, value) {
    const parsed = NotificationMatrix.safeParse(stored);
    const base = parsed.success ? parsed.data : {};
    return { ...base, [type]: { ...(base[type] ?? {}), [channel]: value } };
}
export const CHANNEL_LABELS = { inApp: 'In app', push: 'Push', email: 'Email', digest: 'Daily digest' };
/** The channels a person's matrix shows: guests have no digest (they get no start-of-day summary). */
export function visibleNotificationChannels(opts) {
    return NotificationChannel.options.filter((c) => c !== 'digest' || !opts.guest);
}
/** Whether a type offers this channel (the daily summary has no digest cell). */
export function typeHasChannel(type, channel) {
    return channel !== 'digest' || !NOTIFICATION_TYPES[type].noDigest;
}
/**
 * Where tapping a push or browser notification goes, from its ids: a design notification opens its set at the comment
 * (the team's Feedback, or a guest's review page: the caller decides by who is signed in), a ticket opens the ticket, a
 * comment or a task nudge opens the task, a message opens its channel at the message, anything else the Notifications
 * centre. Decided by entity, not by a list of kinds, so a new kind with the same ids opens the right place.
 */
export function pushTarget(d) {
    if (d.designSetId)
        return { kind: 'design', setId: d.designSetId, commentId: d.designCommentId ?? null };
    if (d.todoItemId)
        return { kind: 'myday', itemId: d.todoItemId };
    if (d.ticketId)
        return { kind: 'task', id: d.ticketId };
    const taskFirst = d.kind === 'task_comment' || d.kind === 'ticket_email' || !d.messageId;
    if (d.workItemId && taskFirst)
        return { kind: 'task', id: d.workItemId };
    if (d.channelId)
        return { kind: 'channel', channelId: d.channelId, messageId: d.messageId ?? null };
    return { kind: 'inbox' };
}
//# sourceMappingURL=notification-prefs.js.map